<script setup lang="ts">
import clone from 'clone'
import { isEqual } from 'lodash-es'
import type { ButtonProps } from 'primevue/button'

import { ArgentoInsurancePaymentPeriodicities, type CoveragePlan } from '~/config'
import { useQuoter } from '~/layers/forms/composables/useQuoter'
import { Coverage } from '~/layers/forms/models'
import type { CoverageComparatorItemExpose } from '~/layers/forms/types/coverageComparatorDefinedExpose'

interface Props {
  argentoInsuranceData: SiteApiSchema<'BrokerProductoGroupByItemModel'>
  insurable: Insurable
  selected: boolean
  isUserChosing: boolean
  coverageTypeCode: string | null
  syncAccordionValue?: ('benefits' | 'details')[]
}

const props = defineProps<Props>()
const emits = defineEmits(['selected', 'accordionChange'])

const { LocalConfigurationStore, RemoteConfigurationStore } = stores()

const company = computed(() =>
  LocalConfigurationStore.value?.associatedCompanies?.find(
    (ac) => ac.argentoInsuranceId == props.argentoInsuranceData.emisorId
  )
)

const companyPlans = computed(
  () =>
    company.value?.avaliablePlans[
      RemoteConfigurationStore.value?.ramoEnum?.toLocaleLowerCase() as Lowercase<
        SiteApiSchema<'RamoCobertura'>
      >
    ]
)

const { cloned: selectedPlanOption, sync: clearSelectedPlan } = useCloned<CoveragePlan | null>(null)

const { quote, quotationStatus, selectedCoverage, clearQuotation, planOptions, resetPlanOptions } =
  useQuoter({
    coveragePlan: selectedPlanOption ?? undefined,
    brokerProductoDataOrigin: computed(() => props.argentoInsuranceData),
    insurable: computed(() => props.insurable),
    coverageTypeCode: computed(() => props.coverageTypeCode ?? null),
  })

const selectedPlanOptionData = computed(() =>
  planOptions.value.find((data) => data.planData.code === selectedPlanOption.value?.code)
)

const canPlanBeSelected = computed(() => selectedPlanOptionData.value?.status === 'success')

const isPlanDisabled = computed(() => {
  const notAllowedStatuses: (CoveragePlanStatus | undefined)[] = ['pending', 'unavailable']
  return (
    notAllowedStatuses.includes(selectedPlanOptionData.value?.status) ||
    !props.insurable.quotationParams?.completedInitialParams
  )
})

const getPlanButtonSeverity = (plan: CoveragePlanOption): ButtonProps['severity'] => {
  if (selectedPlanOption.value?.code === plan.planData.code)
    return plan.status === 'error' ? 'danger' : 'primary'
  return 'secondary'
}

const getPlanButtonStatusIcon = (status: CoveragePlanStatus) => {
  if (['error'].includes(status)) return getIconClass('ph--x-circle-fill')
  if (status === 'success') return getIconClass('ph--check-circle-fill')
  return ''
}

const helperMessage = computed(() => {
  let message = ''
  let severity = ''
  const quotationCompletedInitialParams = !props.insurable.quotationParams
    ? true
    : props.insurable.quotationParams.completedInitialParams

  if (!quotationCompletedInitialParams) {
    message = 'Por favor, complete los datos para empezar a cotizar.'
    severity = 'warn'
  } else {
    switch (selectedPlanOptionData.value?.status) {
      case 'error':
        message = `${props.argentoInsuranceData.emisorNombre} no pudo cotizar la cobertura del seguro.`
        severity = 'error'
        break
      case 'unavailable':
        message = `${props.argentoInsuranceData.emisorNombre} no tiene disponible una cobertura de seguro para el plan ${selectedPlanOptionData.value?.planData.name}.`
        severity = 'warn'
        break
      default:
        message = 'Seleccione un plan'
        severity = 'secondary'
    }
  }

  const validStatus: CoveragePlanStatus[] = ['pending', 'success']

  return {
    hasIssue:
      !quotationCompletedInitialParams ||
      !selectedPlanOptionData.value?.status ||
      !validStatus.includes(selectedPlanOptionData.value?.status),
    severity,
    message,
  }
})

const fetchPlan = useDebounceFn(
  async ({
    planCode = selectedPlanOptionData.value?.planData.code ?? companyPlans.value?.default,
    autoSelect = false,
    overrideQuotation = false,
  }: {
    planCode?: string
    autoSelect?: boolean
    overrideQuotation?: boolean
  } = {}) => {
    if (planOptions.value.length === 1) planCode = planOptions.value[0]?.planData.code
    if (!planCode) return
    const plan = planOptions.value.find((p) => p.planData.code === planCode)
    if (!plan) return
    selectedPlanOption.value = plan.planData

    const planCanBeQuoted = plan.status !== 'unavailable'
    const planQuoted = plan.status === 'success'
    const planQuotationCanBeOverridden = planQuoted && overrideQuotation
    const completeQuotationdInitialParams = props.insurable.quotationParams?.completedInitialParams

    const isReadyToQuote =
      completeQuotationdInitialParams &&
      planCanBeQuoted &&
      (!planQuoted || planQuotationCanBeOverridden)

    if (!isReadyToQuote) return

    plan.status = 'pending'

    await quote()

    if (autoSelect) selectCoverage(true)
    const { argentoInsuranceId, grandTotal } = selectedCoverage.value ?? {}

    if (!argentoInsuranceId || !grandTotal || quotationStatus.value !== 'success') {
      plan.status = 'error'
      return
    }

    plan.status = 'success'
  },
  150
)

const defaultPaymentCondition = computed(() => ({
  argentoInsurancePaymentCondition:
    selectedCoverage.value?.argentoInsuranceAplicablePaymentConditions?.find(
      (pc) => pc.orden === 0
    ),
  argentoInsuranceCalculatedPaymentConditions:
    selectedCoverage.value?.argentoInsuranceAplicableCalculatedPaymentConditions?.find(
      (pc) => pc.orden === 0
    ),
}))

const defaultPaymentPeriodicity = computed(() => {
  const { argentoInsuranceCalculatedPaymentConditions, argentoInsurancePaymentCondition } =
    defaultPaymentCondition.value
  const paymentPeriodicity =
    argentoInsuranceCalculatedPaymentConditions?.frecuenciaPago ??
    argentoInsurancePaymentCondition?.frecuenciaPago

  return Object.values(ArgentoInsurancePaymentPeriodicities).find(
    (pp) => pp.id.toString() === paymentPeriodicity
  )?.value
})

const { getTotalPayment, getPaymentInstallments } = useCoveragePaymentSumary({
  amount: computed(() => selectedCoverage.value?.grandTotal),
  periodicity: defaultPaymentPeriodicity,
  argentoInsurancePaymentCondition: computed(
    () => defaultPaymentCondition.value.argentoInsurancePaymentCondition?.condicionPago
  ),
  argentoInsuranceCalculatedPaymentConditions: computed(
    () => defaultPaymentCondition.value.argentoInsuranceCalculatedPaymentConditions
  ),
})

const defaultPaymentInstallments = computed(() => {
  const installments = getPaymentInstallments()

  const initial = installments?.find((i) => i.order === 0)?.amount

  const filteredInstallments = installments?.filter((i) => i.order !== 0)
  const installmentsCount = filteredInstallments?.length
  const installmentsAmount = filteredInstallments?.every((i) =>
    filteredInstallments?.every((j) => Math.floor(j.amount) === Math.floor(i.amount))
  )
    ? Math.round(filteredInstallments?.[0]?.amount ?? 0)
    : null

  return {
    initial,
    installmentsCount,
    installmentsAmount,
  }
})

const selectCoverage = (pasive = false) => {
  const data: { coverage: Coverage; pasive: boolean } = {
    coverage: Coverage.load(selectedCoverage.value),
    pasive,
  }
  emits('selected', data)
}

const reset = useDebounceFn(() => {
  if (!props.isUserChosing) return
  clearQuotation()
  clearSelectedPlan()
  resetPlanOptions()

  if (
    planOptions.value.length === 1 &&
    props.insurable.quotationParams?.completedInitialParams &&
    quotationStatus.value !== 'pending'
  )
    fetchPlan()
}, 250)

const accordionRef = ref<('benefits' | 'details')[]>(['benefits'])

watch(
  [() => props.insurable.quotationParams?.initialParams, () => props.coverageTypeCode],
  (n, o) => {
    if (!isEqual(n, o)) reset()
  },
  { deep: true }
)

watch(
  computed(() => props.insurable.quotationParams?.finalParams),
  (n, o) => {
    const { isUserChosing, selected: isSelected } = props
    if (isEqual(n, o) || !props.insurable.quotationParams?.completedFinalParams) return

    if (!isUserChosing && isSelected) fetchPlan({ autoSelect: true, overrideQuotation: true })
  },
  {
    deep: true,
  }
)

watch(
  accordionRef,
  () => {
    emits('accordionChange', clone(accordionRef.value))
  },
  { deep: true }
)

watch(
  () => props.syncAccordionValue,
  (n) => {
    if (n && n?.join(',') !== accordionRef.value.join(',')) {
      accordionRef.value = n
    }
  },
  { deep: true }
)

defineExpose<CoverageComparatorItemExpose>({
  fetchPlan,
  getQuotationStatus: () => selectedPlanOptionData.value?.status ?? null,
  getCompanyId: () => props.argentoInsuranceData.emisorId,
})
</script>

<template>
  <CardComponent
    bg="light"
    class="relative"
  >
    <div class="absolute right-0 top-0 z-10">
      <slot name="action" />
    </div>
    <template #header>
      <div class="relative">
        <ImageComponent
          v-if="company?.logoWhiteUrl"
          :src="getCompanyLogo(company?.logoWhiteUrl)"
          class="md:my-3 aspect-[16/4] h-14 object-cover"
        />
        <div
          v-else
          class="my-3 flex flex-col h-14 items-center text-center text-2xl font-bold leading-tight"
        >
        </div>
      </div>
    </template>
    <div class="flex flex-col w-full gap-3 p-2">
      <FormsCoverageComparatorResumeMessage
        v-if="helperMessage.hasIssue || !selectedPlanOption"
        v-bind="helperMessage"
      />

      <div
        v-if="
          selectedPlanOptionData?.status === 'pending' || (selectedCoverage && getTotalPayment())
        "
        class="flex flex-wrap justify-center gap-1 items-center min-h-[7rem]"
      >
        <div
          v-if="selectedPlanOptionData?.status === 'pending'"
          class="min-h-[7rem] flex items-center w-full"
        >
          <ProcessingComponent loading-message="Cotizando... Espere unos minutos." />
        </div>
        <template v-else-if="selectedCoverage && getTotalPayment()">
          <CoveragePrice
            :amount="getTotalPayment()"
            :periodicity="Coverage.getPeriodicityList().default?.value"
          />
          <FormsCoverageComparatorResumePaymentsInstallments
            v-if="!selectedPlanOptionData?.planData.isBasic"
            v-bind="defaultPaymentInstallments"
          />
        </template>
      </div>

      <div>
        <GridComponent
          :min-cols="planOptions.length"
          gap="xs"
        >
          <template v-if="planOptions.length > 1">
            <div class="text-md font-bold col-span-full">Planes</div>
            <template
              v-for="opt in planOptions"
              :key="opt.planData.argentoInsuranceid"
            >
              <div>
                <PvButton
                  :severity="getPlanButtonSeverity(opt)"
                  :outlined="opt.status === 'unavailable'"
                  class="h-12 w-full content-center"
                  :disabled="isPlanDisabled || opt.status === 'unavailable'"
                  @click="fetchPlan({ planCode: opt.planData.code })"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center justify-center flex-nowrap text-nowrap">
                      <span class="flex items-center">
                        <span
                          v-if="getPlanButtonStatusIcon(opt.status)"
                          class="text-[0.8rem]"
                          :class="getPlanButtonStatusIcon(opt.status)"
                        />
                      </span>
                      <span class="text-[0.8rem] font-bold uppercase leading-none">
                        {{ opt.planData.name }}
                      </span>
                    </div>
                    <div class="text-[0.6rem] leading-none">
                      {{ opt.status === 'unavailable' ? 'No disponible' : opt.planData.hint }}
                    </div>
                  </div>
                </PvButton>
              </div>
            </template>
          </template>
          <div class="col-span-full flex flex-col gap-2">
            <PvButton
              :disabled="!canPlanBeSelected || selectedPlanOptionData?.status === 'error'"
              @click="selectCoverage()"
            >
              <span class="text-md py-3 text-[1rem] font-bold uppercase">Contratar</span>
            </PvButton>
          </div>
        </GridComponent>
      </div>

      <PvDivider />

      <template
        v-if="
          selectedPlanOption?.code &&
          LocalConfigurationStore?.coverageBenefitsConfig?.some((cb) =>
            cb.visible.includes('highlight')
          )
        "
      >
        <div class="flex flex-col gap-1">
          <template
            v-for="benefit in LocalConfigurationStore?.coverageBenefitsConfig?.filter((cb) =>
              cb.visible.includes('highlight')
            )"
            :key="benefit.identifier"
          >
            <FormsCoverageComparatorResumeBenefit
              v-if="benefit"
              :benefit
              :emisor-id="argentoInsuranceData.emisorId"
              :plan-code="selectedPlanOption?.code"
              template="highlight"
            />
          </template>
        </div>
        <PvDivider />
      </template>

      <div class="flex flex-col gap-2">
        <span class="text-md font-bold text-surface-500 dark:text-white">Coberturas</span>
        <GridComponent
          :cols="4"
          :min-cols="3"
        >
          <template
            v-for="benefit in LocalConfigurationStore?.coverageBenefitsConfig?.filter((cb) =>
              cb.visible.includes('coverages')
            )"
            :key="benefit.identifier"
          >
            <FormsCoverageComparatorResumeBenefit
              v-if="benefit"
              :benefit
              :emisor-id="argentoInsuranceData.emisorId"
              :plan-code="selectedPlanOption?.code"
              template="trait"
              show-bar
            />
          </template>
        </GridComponent>
      </div>
      <PvDivider />
      <PvAccordion
        v-model:value="accordionRef"
        multiple
        class="flex flex-col gap-4"
      >
        <template #collapseicon>
          <span class="text-md icon-[ph--caret-circle-up-bold]" />
        </template>
        <template #expandicon>
          <span class="text-md icon-[ph--caret-circle-down-bold]" />
        </template>
        <PvAccordionPanel value="benefits">
          <PvAccordionHeader>
            <span class="text-surface-500 dark:text-white">Beneficios</span>
          </PvAccordionHeader>
          <PvAccordionContent>
            <GridComponent
              :cols="4"
              :min-cols="3"
            >
              <template
                v-for="benefit in LocalConfigurationStore?.coverageBenefitsConfig?.filter((cb) =>
                  cb.visible.includes('benefits')
                )"
                :key="benefit.tag ?? benefit.text"
              >
                <FormsCoverageComparatorResumeBenefit
                  v-if="benefit"
                  :benefit
                  :emisor-id="argentoInsuranceData.emisorId"
                  :plan-code="selectedPlanOption?.code"
                  template="trait"
                />
              </template>
            </GridComponent>
          </PvAccordionContent>
        </PvAccordionPanel>
        <template
          v-if="
            selectedPlanOption?.code &&
            selectedPlanOptionData?.status !== 'unavailable' &&
            LocalConfigurationStore?.coverageBenefitsConfig?.some((cb) =>
              cb.visible.includes('details')
            )
          "
        >
          <PvDivider />
          <PvAccordionPanel value="details">
            <PvAccordionHeader>
              <span class="text-surface-500 dark:text-white">Detalles de coberturas</span>
            </PvAccordionHeader>
            <PvAccordionContent>
              <div class="flex flex-col gap-2">
                <template
                  v-for="benefit in LocalConfigurationStore?.coverageBenefitsConfig?.filter((cb) =>
                    cb.visible.includes('details')
                  )"
                  :key="benefit.identifier"
                >
                  <FormsCoverageComparatorResumeBenefit
                    v-if="benefit"
                    :benefit
                    :emisor-id="argentoInsuranceData.emisorId"
                    :plan-code="selectedPlanOption?.code"
                    template="bar"
                  />
                </template>
              </div>
            </PvAccordionContent>
          </PvAccordionPanel>
        </template>
      </PvAccordion>
    </div>
    <slot name="extra-info" />
  </CardComponent>
</template>
